import behaviourPointHistoryConstants from "@/constants/behaviourPointHistoryConstants.js"
import {
  mapCategoryColorToIndicator,
  resolveCategoryColor
} from "@/helpers/behaviourCoreValueForm.js"
import helpers from "@/helpers/index.js"
import moment from "moment-timezone"

const { TRANSACTION_SCOPES, TRANSACTION_TYPES } = behaviourPointHistoryConstants

/**
 * @param {unknown} value
 * @returns {number[]}
 */
export function normalizePointHistoryGradYearValues(value) {
  if (!Array.isArray(value) || !value.length) {
    return []
  }

  return value
    .map((item) => {
      if (item != null && typeof item === "object") {
        return item?.value ?? item?.label ?? item?.id
      }
      return item
    })
    .map((year) => Number(year))
    .filter((year) => Number.isFinite(year))
}

/**
 * @param {import('moment').Moment | null | undefined} date
 * @returns {string}
 */
export function formatPointHistoryApiDate(date) {
  if (!date?.isValid?.()) {
    return ""
  }
  return date.format("YYYY-MM-DD")
}

/** Allowed values for point history transactions list `sort` query param. */
export const POINT_HISTORY_SORT_FIELDS = Object.freeze([
  "activity",
  "core_value_name",
  "points",
  "transaction_at"
])

/** Allowed values for point history transactions list `order` query param. */
export const POINT_HISTORY_SORT_ORDERS = Object.freeze(["asc", "desc"])

/**
 * Maps a table column key to the API `sort` value.
 * @param {string|null|undefined} columnKey
 * @returns {string|null}
 */
export function mapPointHistorySortKey(columnKey) {
  switch (String(columnKey ?? "").trim()) {
    case "activity":
      return "activity"
    case "coreValueName":
      return "core_value_name"
    case "points":
      return "points"
    case "occurredAt":
      return "transaction_at"
    default:
      return null
  }
}

/**
 * Normalizes the PASS-8103 staff/admin transaction scope.
 * @param {unknown} value
 * @returns {"mine" | "schoolWide" | ""}
 */
export function mapPointHistoryScope(value) {
  const normalized = String(value ?? "")
    .trim()
    .replace(/[-_]/g, "")
    .toLowerCase()

  if (normalized === TRANSACTION_SCOPES.MINE) {
    return TRANSACTION_SCOPES.MINE
  }

  if (normalized === "schoolwide") {
    return TRANSACTION_SCOPES.SCHOOL_WIDE
  }

  return ""
}

/**
 * @param {{
 *   scope?: "mine" | "schoolWide" | null,
 *   fromDate?: string,
 *   toDate?: string,
 *   page?: number,
 *   perPage?: number,
 *   search?: string,
 *   gradYears?: unknown,
 *   coreValueId?: string | number | null,
 *   positiveBehaviorId?: string | number | null,
 *   transactionType?: string | null,
 *   studentIds?: unknown,
 *   awardedByUserIds?: unknown,
 *   sortBy?: string|null,
 *   sortOrder?: string|null
 * }} args
 * @returns {Record<string, string | number>}
 */
export function buildPointHistoryTransactionsParams({
  scope = null,
  fromDate = "",
  toDate = "",
  page = 1,
  perPage = 25,
  search = "",
  gradYears = [],
  coreValueId = null,
  positiveBehaviorId = null,
  transactionType = null,
  studentIds = null,
  awardedByUserIds = null,
  sortBy = null,
  sortOrder = null
} = {}) {
  const params = {
    from_date: String(fromDate ?? "").trim(),
    to_date: String(toDate ?? "").trim(),
    page: Math.max(1, Number(page) || 1),
    per_page: Math.max(1, Number(perPage) || 25)
  }

  const normalizedScope = mapPointHistoryScope(scope)
  if (Object.values(TRANSACTION_SCOPES).includes(normalizedScope)) {
    params.scope = normalizedScope
  }

  const q = String(search ?? "").trim()
  if (q) {
    params.search = q
  }

  const gradYearValues = normalizePointHistoryGradYearValues(gradYears)
  gradYearValues.forEach((year, index) => {
    params[`grad_year[${index}]`] = year
  })

  const coreValueRaw = String(coreValueId ?? "").trim()
  if (coreValueRaw && coreValueRaw !== "all") {
    const coreValueNumeric = Number(coreValueRaw)
    if (Number.isFinite(coreValueNumeric)) {
      params.core_value_id = coreValueNumeric
    }
  }

  const positiveBehaviorRaw = String(positiveBehaviorId ?? "").trim()
  if (positiveBehaviorRaw && positiveBehaviorRaw !== "all") {
    const positiveBehaviorNumeric = Number(positiveBehaviorRaw)
    if (Number.isFinite(positiveBehaviorNumeric)) {
      params.positive_behavior_id = positiveBehaviorNumeric
    }
  }

  const normalizedTransactionType = mapApiTransactionType(transactionType)
  if (Object.values(TRANSACTION_TYPES).includes(normalizedTransactionType)) {
    params.type = normalizedTransactionType
  }

  const studentIdValues = normalizePointHistoryGradYearValues(studentIds)
  studentIdValues.forEach((studentId, index) => {
    params[`student_ids[${index}]`] = studentId
  })

  const awardedByIds = normalizePointHistoryGradYearValues(awardedByUserIds)
  awardedByIds.forEach((userId, index) => {
    params[`awarded_by_user_ids[${index}]`] = userId
  })

  const sortByValue = String(sortBy ?? "").trim()
  const sortOrderValue = String(sortOrder ?? "")
    .trim()
    .toLowerCase()
  if (
    POINT_HISTORY_SORT_FIELDS.includes(sortByValue) &&
    POINT_HISTORY_SORT_ORDERS.includes(sortOrderValue)
  ) {
    params.sort = sortByValue
    params.order = sortOrderValue
  }

  return params
}

/**
 * Same filter and pagination params as the transactions list.
 * @param {Parameters<typeof buildPointHistoryTransactionsParams>[0]} args
 * @returns {Record<string, string | number>}
 */
export function buildPointHistoryExportParams(args = {}) {
  return buildPointHistoryTransactionsParams(args)
}

/**
 * @param {unknown} value
 * @returns {string}
 */
export function mapApiTransactionType(value) {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase()

  switch (normalized) {
    case "award":
    case "awarded":
      return TRANSACTION_TYPES.AWARD
    case "redeem":
    case "redemption":
    case "redeemed":
      return TRANSACTION_TYPES.REDEEM
    case "correction":
    case "corrected":
      return TRANSACTION_TYPES.CORRECTION
    default:
      return normalized || ""
  }
}

/**
 * @param {Record<string, unknown> | null | undefined} item
 * @returns {boolean}
 */
export function isPointHistoryRedeemTransaction(item) {
  const type = mapApiTransactionType(
    item?.transactionType ?? item?.transaction_type ?? item?.type
  )
  return type === TRANSACTION_TYPES.REDEEM
}

/**
 * @param {Record<string, unknown> | null | undefined} item
 * @returns {boolean}
 */
export function isPointHistoryCorrectionTransaction(item) {
  const type = mapApiTransactionType(
    item?.transactionType ?? item?.transaction_type ?? item?.type
  )
  return type === TRANSACTION_TYPES.CORRECTION
}

/**
 * @param {unknown} value
 * @returns {string}
 */
export function formatPointHistoryOccurredAt(value) {
  if (value == null || value === "") {
    return ""
  }

  const parsed = moment(value)
  if (!parsed.isValid()) {
    return String(value).trim()
  }

  return helpers.transformDate(parsed, "MM/DD/YYYY h:mm A")
}

/**
 * @param {Record<string, unknown>} item
 * @returns {Record<string, unknown>}
 */
export function mapApiTransactionToRow(item) {
  const student =
    item?.student && typeof item.student === "object" ? item.student : {}
  const awardedByUser =
    item?.awarded_by_user && typeof item.awarded_by_user === "object"
      ? item.awarded_by_user
      : item?.awarded_by && typeof item.awarded_by === "object"
        ? item.awarded_by
        : {}

  const studentName = String(
    item?.student_name ??
      student?.name ??
      student?.full_name ??
      [student?.first_name, student?.last_name].filter(Boolean).join(" ") ??
      ""
  ).trim()

  const transactionType = mapApiTransactionType(
    item?.transaction_type ?? item?.type ?? item?.transactionType
  )

  const reason = String(item?.reason ?? "").trim()

  const descriptionText = String(
    item?.activity ?? item?.description ?? ""
  ).trim()

  const positiveBehaviorName = String(
    item?.positive_behavior?.name ?? item?.positive_behavior_name ?? ""
  ).trim()

  const apiNotes = String(item?.notes ?? "").trim()

  let activity = String(
    positiveBehaviorName ||
      descriptionText ||
      item?.positive_behavior?.name ||
      item?.positive_behavior_name ||
      ""
  ).trim()

  let notes = apiNotes

  if (transactionType === TRANSACTION_TYPES.REDEEM) {
    notes = reason || descriptionText || apiNotes
    activity = ""
  } else if (transactionType === TRANSACTION_TYPES.CORRECTION) {
    notes = reason || descriptionText || apiNotes
    activity = ""
  }

  const awardedBy = String(
    item?.awarded_by_user_name ??
      item?.awarded_by_name ??
      item?.from ??
      awardedByUser?.name ??
      awardedByUser?.full_name ??
      [awardedByUser?.first_name, awardedByUser?.last_name]
        .filter(Boolean)
        .join(" ") ??
      item?.awarded_by ??
      ""
  ).trim()

  const occurredAtRaw =
    item?.occurred_at ??
    item?.transaction_at ??
    item?.transaction_date ??
    item?.date_time ??
    item?.created_at ??
    ""

  const coreValue =
    item?.core_value && typeof item.core_value === "object"
      ? item.core_value
      : {}

  const category = String(
    item?.category ?? coreValue?.category ?? item?.core_value_category ?? ""
  ).trim()

  const categoryColor = resolveCategoryColor(category)

  const coreValueName = String(
    item?.core_value_name ?? coreValue?.name ?? ""
  ).trim()

  return {
    id: item?.id,
    studentName,
    activity,
    points: Number(item?.points ?? item?.points_amount ?? 0) || 0,
    transactionType,
    awardedBy,
    notes,
    occurredAt: formatPointHistoryOccurredAt(occurredAtRaw),
    coreValueId:
      item?.core_value_id ?? coreValue?.id ?? item?.coreValueId ?? null,
    coreValueName,
    category,
    category_color: categoryColor,
    indicator_color: categoryColor
      ? mapCategoryColorToIndicator(categoryColor)
      : undefined,
    gradYear: item?.grad_year ?? item?.gradYear ?? null
  }
}

/**
 * @param {Record<string, unknown> | null | undefined} body
 * @returns {{ pointsAwarded: number, pointsRedeemed: number }}
 */
export function parsePointsSummaryResponse(body) {
  const data = body?.data ?? body ?? {}

  return {
    pointsAwarded: Number(data?.points_awarded) || 0,
    pointsRedeemed: Number(data?.points_redeemed) || 0
  }
}

/**
 * @param {Record<string, unknown> | null | undefined} body
 * @returns {{
 *   totalPointsEarned: number,
 *   pointsUsedOnRewards: number,
 *   remainingPoints: number
 * }}
 */
export function parseStudentPointsSummaryResponse(body) {
  const data = body?.data ?? body ?? {}

  return {
    totalPointsEarned: Number(data?.total_points_earned) || 0,
    pointsUsedOnRewards: Number(data?.points_used_on_rewards) || 0,
    remainingPoints: Number(data?.remaining_points) || 0
  }
}

/**
 * @param {Record<string, unknown> | null | undefined} body
 * @returns {Record<string, number>}
 */
export function mapApiPointHistoryStats(body) {
  const stats =
    body?.statistics ??
    body?.summary ??
    body?.stats ??
    body?.meta?.statistics ??
    {}

  return {
    schoolWideAwarded:
      Number(
        stats?.school_wide_points_awarded ??
          stats?.school_wide_awarded ??
          stats?.schoolWideAwarded
      ) || 0,
    schoolWideRedeemed:
      Number(
        stats?.school_wide_points_redeemed ??
          stats?.school_wide_redeemed ??
          stats?.schoolWideRedeemed
      ) || 0,
    pointsYouAwarded:
      Number(
        stats?.points_you_awarded ??
          stats?.points_awarded_by_you ??
          stats?.pointsYouAwarded
      ) || 0,
    pointsRedeemed:
      Number(stats?.points_redeemed ?? stats?.pointsRedeemed) || 0,
    totalPointsEarned:
      Number(
        stats?.total_points_earned ??
          stats?.total_points ??
          stats?.totalPointsEarned
      ) || 0,
    pointsUsedOnRewards:
      Number(
        stats?.points_used_on_rewards ??
          stats?.points_redeemed ??
          stats?.pointsUsedOnRewards
      ) || 0,
    remainingPoints:
      Number(stats?.remaining_points ?? stats?.remainingPoints) || 0
  }
}

/**
 * @param {Record<string, unknown> | null | undefined} body
 * @returns {{ rows: Record<string, unknown>[], pagination: Record<string, unknown>, stats: Record<string, number> }}
 */
export function parsePointHistoryTransactionsResponse(body) {
  const rows = (body?.data ?? []).map(mapApiTransactionToRow)
  return {
    rows,
    pagination: body?.pagination || body?.meta || {},
    stats: mapApiPointHistoryStats(body)
  }
}
